/**
 * Plein ecran : automatique au lancement d'une partie sur telephone, mais JAMAIS contre la volonte du joueur.
 *
 * Des que le joueur en sort (bouton « Quitter le plein ecran », geste/bouton Retour du telephone, Echap...), il est note comme
 * « refuse » et plus aucun appel automatique ne l'y remet (premier tap, lancement d'une partie, ouverture d'un jeu depuis l'accueil) :
 * seul le bouton « Plein ecran » (`toggleFullscreen`) le reactive. Ce refus est garde dans `sessionStorage` (partage avec l'accueil et
 * les autres jeux du meme onglet, meme origine) ; sans stockage disponible, il vaut pour la page.
 *
 * Certains navigateurs mobiles remettent aussi la page en plein ecran d'eux-memes des qu'on y revient (changement d'appli, notification,
 * tiroir systeme...), simplement parce qu'elle l'etait avant — sans le moindre appel a requestFullscreen() de notre part. On distingue
 * donc nos propres transitions (drapeau pose juste avant chaque appel ci-dessous) de celles imposees par le navigateur : si ce dernier
 * nous replace en plein ecran alors que le joueur l'a refuse, on en ressort aussitot.
 */
const DECLINED_KEY = "dkg:fullscreen-declined";

/** Repli quand `sessionStorage` est indisponible (navigation privee, iframe bloquee). */
let declinedInMemory = false;
/** Vrai entre l'appel de l'une de nos fonctions et l'evenement `fullscreenchange` qu'elle provoque. */
let ourChangePending = false;

function isDeclined(): boolean {
  try {
    return window.sessionStorage.getItem(DECLINED_KEY) === "1" || declinedInMemory;
  } catch {
    return declinedInMemory;
  }
}

function setDeclined(declined: boolean): void {
  declinedInMemory = declined;
  try {
    if (declined) window.sessionStorage.setItem(DECLINED_KEY, "1");
    else window.sessionStorage.removeItem(DECLINED_KEY);
  } catch {
    // Stockage indisponible : le refus vaut pour la page.
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("fullscreenchange", () => {
    if (ourChangePending) {
      // Transition voulue par nous : le refus a deja ete mis a jour par l'appelant.
      ourChangePending = false;
      return;
    }
    if (document.fullscreenElement) {
      if (isDeclined()) void document.exitFullscreen().catch(() => undefined);
    } else {
      setDeclined(true);
    }
  });
}

/** Bouton « Plein ecran » : choix explicite du joueur, dans un sens comme dans l'autre. Retourne le nouvel etat. */
export async function toggleFullscreen(): Promise<boolean> {
  if (!document.fullscreenElement) {
    setDeclined(false);
    ourChangePending = true;
    await document.documentElement.requestFullscreen().catch(() => undefined);
    if (!document.fullscreenElement) ourChangePending = false;
  } else {
    setDeclined(true);
    ourChangePending = true;
    await document.exitFullscreen().catch(() => undefined);
    if (document.fullscreenElement) ourChangePending = false;
  }
  return !!document.fullscreenElement;
}

export function isFullscreen(): boolean {
  return !!document.fullscreenElement;
}

/** Ecran tactile etroit (telephone) : c'est la ce qui declenche le plein ecran auto au lancement d'une partie. */
export function isMobileDevice(): boolean {
  return window.matchMedia("(pointer: coarse) and (max-width: 640px)").matches;
}

/**
 * Plein ecran automatique : sans effet si deja actif, si le joueur l'a refuse, ou si le navigateur refuse
 * (ex: hors interaction utilisateur).
 */
export async function requestFullscreen(): Promise<void> {
  if (document.fullscreenElement || isDeclined()) return;
  ourChangePending = true;
  await document.documentElement.requestFullscreen().catch(() => undefined);
  if (!document.fullscreenElement) ourChangePending = false;
}
